import { map, Observable, tap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Marshal } from '../models/marshal';
import { SportType, Race } from '../models/race';
import { RaceClass } from '../models/race-class';
import { RateData } from 'src/app/models/types';
import { RaceRaw } from '../models/interfaces/race_raw.interface';
import { DocType } from '../modules/race-form/models/doc-type';
import { SettingsService } from '../services/settings.service';
import { ServerResponse } from '../response';
import { RaceClassRaw } from '../models/interfaces/race_class_raw.interface';

@Injectable({
  providedIn: 'root',
})
export class RaceDomainService {
  constructor(
    private settingService: SettingsService,
    private http: HttpClient
  ) {}

  // Получает список видов спорта
  getSportTypes(): Observable<SportType[]> {
    return this.http
      .get<ServerResponse<SportType[]>>(
        `${this.settingService.settings.urlGTM}/api/race/sport_types?lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((res) => res.data)
      );
  }

  // Получает список типов документов, требуемых при регистрации
  getDocTypes(): Observable<DocType[]> {
    return this.http
      .get<ServerResponse<DocType[]>>(
        `${this.settingService.settings.urlGTM}/api/race/doc_types?lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((res) => res.data)
      );
  }

  // Возвращает список гонок за указанный период
  getList(dateFrom: string, dateTo: string, type: string): Observable<Race[]> {
    return this.http
      .get<ServerResponse<RaceRaw[]>>(
        `${this.settingService.settings.urlGTM}/api/race/list?date_from=${dateFrom}&date_to=${dateTo}&type=${type}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((res) => res.data.map((item) => new Race(item))),
        map((arr) => arr.reverse()),
      );
  }

  // Возвращает профиль гонки
  getRace(rUid: string): Observable<Race> {
    return this.http
      .get<ServerResponse<RaceRaw>>(
        `${this.settingService.settings.urlGTM}/api/race?rUid=${rUid}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((res) => new Race(res.data))
      );
  }

  // Сохраняет профиль гонки
  saveRace(race: any, lang: string): Observable<string> {
    return this.http
      .post<ServerResponse<string>>(`${this.settingService.settings.urlGTM}/api/race`, {
        lang,
        race,
      })
      .pipe(
        map((res) => res.data)
      );
  }

  // Удаляет гонку
  deleteRace(rUid: string): Observable<string> {
    return this.http
      .delete<ServerResponse<string>>(
        `${this.settingService.settings.urlGTM}/api/race?rUid=${rUid}`
      )
      .pipe(
        map((response) => {
          if (response.data === 'ERROR') {
            throw new Error(response.data);
          }
          return response.data;
        })
      );
  }

  // Копирует гонку
  copyRace(rUid: string, copyName: string, withDocs: boolean, withCompetitors: boolean): Observable<Race> {
    return this.http
      .post<ServerResponse<RaceRaw>>(
        `${this.settingService.settings.urlGTM}/api/race/copy`,
        {
          rUid,
          copyName,
          withDocs,
          withCompetitors,
          lang: this.settingService.selectedLang,
        }
      )
      .pipe(
        map((response) => new Race(response.data))
      );
  }

  // Получает список классов гонки
  getClasses(rUid: string, forSelector: number = 0): Observable<RaceClass[]> {
    return this.http
      .get<ServerResponse<RaceClassRaw[]>>(
        `${this.settingService.settings.urlGTM}/api/race/class/list?rUid=${rUid}&forSelector=${forSelector}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map((res) => res.data.map((item) => new RaceClass(item)))
      );
  }

  // Сохраняет класс гонки
  saveRaceClass(raceClass: RaceClass, rUid: string | null, clUid: string | null): Observable<void> {
    return this.http
      .post<ServerResponse<void>>(`${this.settingService.settings.urlGTM}/api/race/class`, {
        lang: this.settingService.selectedLang,
        raceClass,
        rUid,
        clUid,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Удаляет класс в гонке
  deleteClass(rUid: string, clUid: string): Observable<void> {
    return this.http
      .delete<ServerResponse<void>>(
        `${this.settingService.settings.urlGTM}/api/race/class?rUid=${rUid}&clUid=${clUid}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет маршала
  saveMarshal(marshal: Marshal): Observable<void> {
    return this.http
      .post<ServerResponse<void>>(`${this.settingService.settings.urlGTM}/api/race/marshal`, {
        rUid: marshal.rUid,
        number: marshal.number,
        lang: this.settingService.selectedLang,
        marshal,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Удаляет маршала
  deleteMarshal(rUid: string, number: number): Observable<void> {
    return this.http
      .delete<ServerResponse<void>>(
        `${this.settingService.settings.urlGTM}/api/race/marshal?rUid=${rUid}&number=${number}&lang=${this.settingService.selectedLang}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Получает список отзывов на гонку
  getReviews(rUid: string): Observable<RateData> {
    return this.http
      .get<{ data: RateData }>(
        `${this.settingService.settings.urlGTM}/api/race/review/list?rUid=${rUid}`
      )
      .pipe(
        map((result) => result.data)
      );
  }

  // Добавляет отзыв на гонку
  addReview(rUid: string, rates: number[], comment: string): Observable<void> {
    return this.http
      .post<void>(`${this.settingService.settings.urlGTM}/api/race/review`, {
        rUid,
        rates,
        comment,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет логотипы протоколов гонки
  saveProtocolLogos(race: any): Observable<void> {
    return this.http
      .patch<ServerResponse<void>>(`${this.settingService.settings.urlGTM}/api/race/protocol_logos`, {
        race,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Изменяет статус гонки
  changeRaceState(rUid: string, state: number): Observable<void> {
    return this.http
      .patch<ServerResponse<void>>(`${this.settingService.settings.urlGTM}/api/race/state`, {
        state,
        rUid,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Получает статус гонки
  getRaceState(rUid: string): Observable<number> {
    return this.http
      .get<ServerResponse<number>>(`${this.settingService.settings.urlGTM}/api/race/state`, {
        params: {
          rUid,
          lang: this.settingService.selectedLang,
        },
      })
      .pipe(
        map((res: ServerResponse<number>) => res.data)
      );
  }
}
